import React from 'react';
import { PiggyBank, Clock, Calendar, Repeat, Settings } from 'lucide-react';

/**
 * Компонент для ввода параметров досрочного погашения
 */
const EarlyRepaymentOptionsSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  earlyRepaymentFrequencies,
  earlyRepaymentTypes
}) => {
  return (
    <div className="mb-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <PiggyBank className="w-5 h-5 mr-2 text-green-500" />
        Параметры досрочного погашения
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Сумма досрочного погашения */}
        <div>
          <label htmlFor="earlyRepaymentAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <PiggyBank className="w-4 h-4 mr-2 text-green-500" />
            Сумма досрочного платежа
          </label>
          <input
            type="text"
            id="earlyRepaymentAmount"
            name="earlyRepaymentAmount"
            value={formData.earlyRepaymentAmount}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.earlyRepaymentAmount ? 'input-error' : ''}`}
            placeholder="Сумма для досрочного погашения"
          />
          {errors.earlyRepaymentAmount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.earlyRepaymentAmount}
            </p>
          )}
        </div>

        {/* Периодичность досрочного погашения */}
        <div>
          <label htmlFor="earlyRepaymentFrequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Repeat className="w-4 h-4 mr-2 text-green-500" />
            Периодичность погашения
          </label>
          <select
            id="earlyRepaymentFrequency"
            name="earlyRepaymentFrequency"
            value={formData.earlyRepaymentFrequency}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.earlyRepaymentFrequency ? 'input-error' : ''}`}
          >
            {earlyRepaymentFrequencies.map((frequency) => (
              <option key={frequency.id} value={frequency.id}>
                {frequency.name}
              </option>
            ))}
          </select>
          {errors.earlyRepaymentFrequency && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.earlyRepaymentFrequency}
            </p>
          )}
        </div>

        {/* Начальный месяц */}
        <div>
          <label htmlFor="earlyRepaymentStartMonth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-green-500" />
            Месяц начала погашения
          </label>
          <input
            type="text"
            id="earlyRepaymentStartMonth"
            name="earlyRepaymentStartMonth"
            value={formData.earlyRepaymentStartMonth}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.earlyRepaymentStartMonth ? 'input-error' : ''}`}
            placeholder="С какого месяца начать"
          />
          {errors.earlyRepaymentStartMonth && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.earlyRepaymentStartMonth}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Номер месяца от начала кредита
          </p>
        </div>

        {/* Опция пересчета */}
        <div className="flex items-start pt-6">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="recalculateAfterEachPayment"
              checked={formData.recalculateAfterEachPayment}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Пересчитывать график после каждого досрочного платежа
            </span>
          </label>
        </div>

        {/* Тип досрочного погашения */}
        <div className="col-span-full mt-2">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            <Settings className="w-4 h-4 mr-2 text-green-500" />
            Цель досрочного погашения
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {earlyRepaymentTypes.map((type) => (
              <label key={type.id} className="relative rounded-lg border-2 bg-white dark:bg-gray-700 p-4 cursor-pointer 
                  flex flex-col transition-colors duration-200
                  ${formData.earlyRepaymentType === type.id 
                    ? 'border-green-500 dark:border-green-500' 
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}">
                <input
                  type="radio"
                  name="earlyRepaymentType"
                  value={type.id}
                  checked={formData.earlyRepaymentType === type.id}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <div>
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">{type.name}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {type.description}
                  </span>
                </div>
                <span className={`absolute top-4 right-4 h-4 w-4 rounded-full ${
                  formData.earlyRepaymentType === type.id 
                    ? 'bg-green-500' 
                    : 'bg-gray-200 dark:bg-gray-600'
                }`}></span>
              </label>
            ))}
          </div>
          {errors.earlyRepaymentType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.earlyRepaymentType}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 bg-green-100 dark:bg-green-900/30 p-3 rounded-lg border border-green-200 dark:border-green-800">
        <div className="flex items-start">
          <PiggyBank className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">
              О досрочном погашении кредита
            </h4>
            <p className="text-xs text-green-600 dark:text-green-400">
              Досрочное погашение может существенно сэкономить на процентах. 
              Уменьшение срока позволяет максимально сократить переплату, 
              а уменьшение платежа — снизить ежемесячную нагрузку. Согласно законодательству РФ, 
              банки не могут брать комиссию за досрочное погашение ипотеки, 
              а для потребительских кредитов ограничения действуют после первых 30 дней.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarlyRepaymentOptionsSection;